// src/hooks/useBloodRangeContext.ts
import { useMemo } from "react";
import { QueryClient, useQuery } from "@tanstack/react-query";

import { fetchBloodTestCatalogue } from "../api/healthcare/Testing/BloodTestCatalogueApi";
import { BloodTestSubCategory, ReferenceRange } from "../types/lab/BloodTestCatalogueTypes";
import { Patient } from "../types/healthcare/PatientTypes";
import { normaliseSex, RangeContext } from "../utils/geneCodes/referenceRange";

export type RangePatient = Pick<Patient, "sex" | "gender" | "date_of_birth">;

export interface BloodRangeContextResult {
	catalogue: BloodTestSubCategory[];
	rangeContext: RangeContext;
	isLoading: boolean;
}

const EMPTY_CATALOGUE: BloodTestSubCategory[] = [];

export const bloodCatalogueQueryOptions = {
	queryKey: ["blood-test-catalogue"] as const,
	queryFn: fetchBloodTestCatalogue,
	staleTime: 1000 * 60 * 60, // 1h
	gcTime: 1000 * 60 * 60 * 24, // 24h
	retry: 1,
};

// Warm the cache once per session so a blood surface never renders against an unloaded catalogue
export const prefetchBloodCatalogue = (queryClient: QueryClient): Promise<void> =>
	queryClient.prefetchQuery(bloodCatalogueQueryOptions);

export const buildRangesByType = (catalogue: BloodTestSubCategory[]): Map<string, ReferenceRange[]> => {
	const map = new Map<string, ReferenceRange[]>();
	catalogue.forEach((sub) => {
		(sub.test_types ?? []).forEach((type) => {
			if (type.value) map.set(type.value, type.reference_ranges ?? []);
		});
	});
	return map;
};

// The API serialises DOB as a string; a Date only arrives from a form, so keep its local calendar day
const dobString = (dob: RangePatient["date_of_birth"] | undefined): string | null => {
	if (!dob) return null;
	if (dob instanceof Date) {
		if (isNaN(dob.getTime())) return null;
		return `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, "0")}-${String(dob.getDate()).padStart(2, "0")}`;
	}
	return String(dob);
};

export const useBloodRangeContext = (patient?: RangePatient | null): BloodRangeContextResult => {
	const { data, isLoading } = useQuery(bloodCatalogueQueryOptions);

	const catalogue = data ?? EMPTY_CATALOGUE;
	const ready = data !== undefined;
	const sex = patient?.sex;
	const gender = patient?.gender;
	const dob = dobString(patient?.date_of_birth);

	const rangeContext = useMemo<RangeContext>(
		() => ({ rangesByType: buildRangesByType(catalogue), sex: normaliseSex(sex, gender), dob, ready }),
		[catalogue, sex, gender, dob, ready],
	);

	return { catalogue, rangeContext, isLoading };
};
